import {
  SUBMISSION_OBSERVATION_FRESHNESS,
  SUBMISSION_STAGE_STATE,
  type RunPreparationSummary,
  type SubmissionProgress,
  type SubmissionStage,
} from "@nova-ai/shared"
import { Check, ChevronRight } from "lucide-react"

import { formatDateTime } from "../../lib/format-date"
import { cn } from "../../lib/utils"
import { Callout } from "../ui/callout"
import { RunCatalogSummary } from "./run-catalog-evidence"
import { RunSubmissionDetails } from "./run-submission-details"

const STEPS = [
  { key: "prepare", label: "Prepare data" },
  { key: "approve", label: "Approve" },
  { key: "send", label: "Send" },
  { key: "verify", label: "Verify" },
] as const

const STATE_LABELS: Record<SubmissionStage["state"], string> = {
  pending: "Not started",
  active: "In progress",
  waiting: "Waiting",
  complete: "Complete",
  blocked: "Blocked",
  failed: "Failed",
}

export function RunSubmissionProgress({
  progress,
  error,
  preparation,
  onHistoryPage,
}: {
  progress: SubmissionProgress | undefined
  error: Error | null
  onHistoryPage?: (offset: number) => void
  preparation: RunPreparationSummary | null | undefined
}) {
  return (
    <section
      aria-label="Run progress"
      aria-busy={!progress && !error}
      className="shrink-0 space-y-3 rounded-xl border border-border bg-card p-4 shadow-card"
    >
      <ol className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {STEPS.map(({ key, label }, index) => (
          <li key={key} className="min-w-0">
            <TrackerStep label={label} index={index} stage={progress?.stages[key]} />
          </li>
        ))}
      </ol>

      {error && (
        <Callout dense tone="warning">
          <p>
            {progress
              ? "Progress refresh failed. Showing saved evidence."
              : "Progress unavailable."}
          </p>

          <p className="break-words">{error.message}</p>

          <p>Retrying automatically.</p>
        </Callout>
      )}

      {progress ? (
        <>
          <SubmissionCounts counts={progress.counts} />

          <RunCatalogSummary progress={progress} />

          <SubmissionNextAction progress={progress} />

          <ObservationFreshness progress={progress} />

          <RunSubmissionDetails
            progress={progress}
            preparation={preparation}
            {...(onHistoryPage ? { onHistoryPage } : {})}
          />
        </>
      ) : (
        !error && <p className="text-sm text-muted-foreground">Loading submission progress…</p>
      )}
    </section>
  )
}

function TrackerStep({
  label,
  index,
  stage,
}: {
  label: string
  index: number
  stage: SubmissionStage | undefined
}) {
  const isComplete = stage?.state === SUBMISSION_STAGE_STATE.complete
  const isActive = stage?.state === SUBMISSION_STAGE_STATE.active
  const needsAttention =
    stage?.state === SUBMISSION_STAGE_STATE.blocked ||
    stage?.state === SUBMISSION_STAGE_STATE.failed
  return (
    <div data-state={stage?.state ?? "unknown"} className="space-y-1 text-xs">
      <div className="flex items-center gap-2">
        {isComplete ? (
          <Check aria-hidden className="size-4 shrink-0 text-success" />
        ) : (
          <span aria-hidden className="w-4 shrink-0 text-center text-muted-foreground tabular-nums">
            {index + 1}
          </span>
        )}

        <span className="text-sm font-medium text-foreground">{label}</span>

        {index < STEPS.length - 1 && (
          <ChevronRight
            aria-hidden
            className="ml-auto hidden size-3 text-muted-foreground sm:block"
          />
        )}
      </div>

      <p
        className={cn(
          "pl-6 text-muted-foreground",
          isActive && "font-medium text-info",
          needsAttention && "font-medium text-foreground"
        )}
      >
        {stage ? STATE_LABELS[stage.state] : "Awaiting evidence"}
      </p>
    </div>
  )
}

function countLabel(count: number | null, noun: string) {
  return count === null ? `${noun}: unknown` : `${count.toLocaleString("en-US")} ${noun}`
}

function SubmissionCounts({ counts }: { counts: SubmissionProgress["counts"] }) {
  const sent = countLabel(counts.sent, "variants sent")
  const sentOfTotal =
    counts.sent !== null && counts.total !== null
      ? `${counts.sent.toLocaleString("en-US")} / ${counts.total.toLocaleString("en-US")} variants sent`
      : `${sent} · ${countLabel(counts.total, "total")}`
  return (
    <div className="space-y-1 text-sm tabular-nums" aria-label="Submission counts">
      <p className="font-medium text-foreground">{sentOfTotal}</p>

      <ul className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
        <li>{countLabel(counts.confirmed, "import confirmed")}</li>

        <li>{countLabel(counts.awaiting, "awaiting confirmation")}</li>

        <li
          className={cn(
            counts.rejected !== null && counts.rejected > 0 && "font-medium text-foreground"
          )}
        >
          {countLabel(counts.rejected, "rejected")}
        </li>

        <li
          className={cn(
            counts.unresolved !== null && counts.unresolved > 0 && "font-medium text-foreground"
          )}
        >
          {countLabel(counts.unresolved, "unresolved")}
        </li>
      </ul>
    </div>
  )
}

function SubmissionNextAction({ progress }: { progress: SubmissionProgress }) {
  return (
    <div className="space-y-1 break-words text-sm text-foreground" aria-live="polite">
      {progress.waitingReason && <p>{progress.waitingReason}</p>}

      {progress.nextAction && <p>{progress.nextAction}</p>}

      {progress.nextActionAt && (
        <p className="text-xs text-muted-foreground">
          {`Next scheduled action: ${formatDateTime(progress.nextActionAt)}`}
        </p>
      )}
    </div>
  )
}

function ObservationFreshness({ progress }: { progress: SubmissionProgress }) {
  const isStale = progress.observationFreshness === SUBMISSION_OBSERVATION_FRESHNESS.stale
  const isUnknown = progress.observationFreshness === SUBMISSION_OBSERVATION_FRESHNESS.unknown
  return (
    <p className="text-xs text-muted-foreground">
      {isStale && (
        <span className="font-medium text-foreground">Import observation is stale. </span>
      )}

      {isUnknown && "Import observation freshness is unknown. "}

      {progress.observedAt
        ? `Import last observed ${formatDateTime(progress.observedAt)}.`
        : "No import observation yet."}
    </p>
  )
}
